/**
 * UserTagEditor — 新建 / 编辑用户标签（ADM-USR-S008）
 *
 * 1:1 对照 design/Novel-admin/src/pages/user-tags.jsx UserTagNew / UserTagEdit。
 * 新建不接收 is_active（落行即 false）。编辑只改 name / description。
 * §9.3 预置 tag_key 只读展示，无编辑输入。
 */

import {
  CreateUserTagRequestSchema,
  USER_TAG_KEY_PATTERN,
  type UserTagDto,
  isPresetUserTagKey,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useUserTagsPage } from '~/data/queries';
import { userTagsApi } from '~/hooks/use-user-tags';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { Switch } from '~/ui/Switch';
import { toast } from '~/ui/Toast';

const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={150} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

export function UserTagEditor({ tag, onClose }: { tag: UserTagDto | null; onClose: () => void }) {
  const isEdit = tag !== null;
  const preset = tag !== null && isPresetUserTagKey(tag.tag_key);
  const qc = useQueryClient();
  const { data: existing } = useUserTagsPage({ page: 1, pageSize: 200 });
  const [tagKey, setTagKey] = useState(tag?.tag_key ?? '');
  const [name, setName] = useState(tag?.name ?? '');
  const [description, setDescription] = useState(tag?.description ?? '');

  const keyOk = USER_TAG_KEY_PATTERN.test(tagKey);
  const dup = !isEdit && existing.some((t) => t.tag_key === tagKey);
  const canSave = isEdit ? !!name : keyOk && !dup && !!name;

  const footerHint = isEdit
    ? 'body 携带 tag_key / is_active / binding_status → VALIDATION_FAILED'
    : !tagKey
      ? 'tag_key 必填 · 正则 ^[a-z][a-z0-9_]{0,63}$'
      : !keyOk
        ? 'VALIDATION_FAILED · tag_key 格式非法'
        : dup
          ? 'RESOURCE_CONFLICT · tag_key 已存在（全局唯一）'
          : !name
            ? '名称必填'
            : '创建后处于未启用状态，启用走启停端点';

  const m = useMutation({
    mutationFn: async () => {
      if (isEdit && tag) {
        return userTagsApi.update(tag.id, { name, description });
      }
      const parsed = CreateUserTagRequestSchema.safeParse({
        tag_key: tagKey,
        name,
        description: description || undefined,
      });
      if (!parsed.success) {
        toast(parsed.error.issues[0]?.message ?? '校验失败', { tone: 'neg' });
        throw new Error('invalid');
      }
      return userTagsApi.create(parsed.data);
    },
    onSuccess: (row) => {
      toast(isEdit ? '标签已保存' : `已创建「${row.name}」· is_active=false（新建强制未启用）`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      void qc.invalidateQueries({ queryKey: ['user-tags'] });
      onClose();
    },
    onError: (e) => {
      if (e instanceof Error && e.message === 'invalid') return;
      toast(e instanceof AdminApiError ? e.message : '保存失败', { tone: 'neg' });
    },
  });

  return (
    <Dialog
      width={620}
      title={isEdit ? `编辑标签 ${tag.tag_key}` : '新建用户标签'}
      subtitle={
        isEdit
          ? 'PATCH /api/v1/user-tags/:id · 只可改 name / description'
          : 'POST /api/v1/user-tags · 落行即 is_active=false · 不接收 app_id'
      }
      icon="IconTag"
      onClose={onClose}
      footer={
        <>
          <div
            style={{ flex: 1, fontSize: 11, color: canSave ? 'var(--fg-muted)' : 'var(--warn)' }}
          >
            {footerHint}
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={m.isPending || !canSave}
            onClick={() => m.mutate()}
          >
            {isEdit ? '保存' : '创建标签'}
          </button>
        </>
      }
    >
      <div style={{ padding: '6px 18px 18px' }}>
        {isEdit ? (
          <FormRow label="tag_key" hint={preset ? '预置标签，tag_key 不可改' : '落行后不可改'}>
            <span className="mono" style={{ fontSize: 12.5 }}>
              {tag.tag_key}
              <span className="chip" style={{ fontSize: 9.5, marginLeft: 6 }}>
                <Icons.IconLock size={8} />
                只读
              </span>
              {preset && (
                <span className="chip" style={{ fontSize: 9.5, marginLeft: 6 }}>
                  预置
                </span>
              )}
            </span>
          </FormRow>
        ) : (
          <FormRow label="tag_key" required hint="稳定机读标识 · 全局唯一 · 落行后不可改">
            <input
              className="input mono"
              value={tagKey}
              onChange={(e) => setTagKey(e.target.value)}
              placeholder="high_value_reader"
              style={{ maxWidth: 320 }}
            />
            {tagKey && (
              <div className="hstack" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                <span
                  className={`chip ${keyOk ? 'chip-pos' : 'chip-neg'}`}
                  style={{ fontSize: 10 }}
                >
                  {keyOk ? '格式合法' : '格式非法'}
                </span>
                <span className={`chip ${dup ? 'chip-neg' : 'chip-pos'}`} style={{ fontSize: 10 }}>
                  {dup ? '已存在' : '未占用'}
                </span>
              </div>
            )}
          </FormRow>
        )}
        <FormRow label="名称" required hint={isEdit ? undefined : '运营内部可读，不下发 C 端用户'}>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </FormRow>
        <FormRow label="说明" hint={isEdit ? undefined : '标签用途说明'}>
          <textarea
            className="input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ height: 'auto' }}
          />
        </FormRow>
        {isEdit ? (
          <FormRow
            label="绑定态 / 启停"
            hint="均为只读字段：binding_status 服务端派生，is_active 走独立启停端点"
          >
            <div className="hstack" style={{ gap: 6 }}>
              <span
                className={`chip ${tag.binding_status === 'bound' ? 'chip-info' : 'chip-warn'} mono`}
                style={{ fontSize: 10 }}
              >
                {tag.binding_status}
              </span>
              <span className={`chip ${tag.is_active ? 'chip-pos' : ''}`} style={{ fontSize: 10 }}>
                is_active={String(tag.is_active)}
              </span>
            </div>
          </FormRow>
        ) : (
          <FormRow label="启停 is_active" hint="创建端点不接受该入参">
            <div className="hstack" style={{ gap: 8 }}>
              <Switch checked={false} disabled />
              <span className="chip chip-warn" style={{ fontSize: 10 }}>
                <Icons.IconLock size={9} />
                落行即 false
              </span>
            </div>
          </FormRow>
        )}
      </div>
    </Dialog>
  );
}
